import React from 'react';
import { UserPlus, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from './HeroSection.module.scss';
import { useNavigate } from 'react-router-dom';

interface HeroSectionProps {
  logo: string;
  isScrolled: boolean;
  scrollY: number;
  onTabChange: (tab: "form" | "offers") => void;
  activeTab: "form" | "offers";
  totalOffers?: number;
  isMobile?: boolean;
  isCompact?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  logo, 
  isScrolled, 
  scrollY,
  onTabChange,
  activeTab,
  totalOffers = 0,
  isMobile = false,
  isCompact = false
}) => {

  const handleTabClick = (tab: "form" | "offers") => {
    onTabChange(tab);
  };

  const navigate = useNavigate();

  // Calcular valores dinámicos basados en el scroll y modo mobile
  const heroHeight = isMobile 
    ? (isCompact ? 'auto' : '100dvh')
    : (isScrolled ? '20vh' : '50vh');
  const heroOpacity = Math.max(0.3, 1 - (scrollY / 300));
  const heroTranslate = isScrolled ? -scrollY * 0.3 : 0;

  // Configuración de animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const buttonVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section 
      className={`${styles.heroSection} ${isMobile && isCompact ? styles.heroSection_compact : ''}`}
      style={!isMobile ? {
        '--hero-height': heroHeight,
        '--hero-opacity': heroOpacity,
        '--hero-translate': `${heroTranslate}px`
      } as React.CSSProperties : {}}
    >
      <motion.div 
        className={styles.heroOverlay}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div 
          className={styles.logoContainer}
          variants={itemVariants}
        >
          <img 
            src={logo || "/placeholder.svg"} 
            alt="Logo de Edify" 
            className={styles.logo} 
            onClick={() => navigate('/')}
          />
        </motion.div>

        <motion.div 
          className={styles.heroContent}
          variants={itemVariants}
        >
          <h1 className={styles.heroTitle}>Construí tu futuro profesional</h1>
          <p className={styles.heroSubtitle}>
            Conectamos profesionales talentosos con las mejores empresas de construcción.
          </p>
        </motion.div>

        {/* Navegación en el hero */}
        <motion.div 
          className={styles.heroNavigation}
          variants={containerVariants}
        >
          <motion.button
            className={`${styles.heroNavButton} ${activeTab === "form" ? styles.active : ""}`}
            onClick={() => handleTabClick("form")}
            variants={buttonVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            {isMobile && isCompact ? (
              <UserPlus size={20} />
            ) : (
              <span className={styles.heroNavButton_text}>Inscribirme</span>
            )}
          </motion.button>
          <motion.button
            className={`${styles.heroNavButton} ${activeTab === "offers" ? styles.active : ""}`}
            onClick={() => handleTabClick("offers")}
            variants={buttonVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            {isMobile && isCompact ? (
              <div className={styles.heroNavButton_iconContainer}>
                <Briefcase size={20} />
                {totalOffers > 0 && (
                  <span className={styles.heroNavButton_badge}>
                    {totalOffers}
                  </span>
                )}
              </div>
            ) : (
              <span className={styles.heroNavButton_text}>
                Ver Ofertas
                {totalOffers > 0 && (
                  <span className={styles.heroNavButton_badge}>
                    {totalOffers}
                  </span>
                )}
              </span>
            )}
          </motion.button>
        </motion.div>

      </motion.div>
    </section>
  );
};

export default HeroSection;
